/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import Classnames from 'classnames';
import React, { Component } from 'react';
import { Popover, Icon } from '@shopify/polaris';
import { MobilePlusMajor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
// import { RenderColorPicker2 } from 'src/components/views/common/forms/render-color-picker-2';
import { RenderColorCustomPicker } from 'src/components/views/common/forms/render-color-custom-picker';
import { getContrastColor, hexToRgb, RGBAToHex, isHexWithAlpha } from 'src/helpers/color-helper';
import { ReactComponent as IconDrop } from 'src/assets/images/shopify-integration-settings/drop.svg';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void,
  disableAlpha: boolean
};

type ComposedProps = Props & WithI18nProps;

class ColorPickerItem extends Component<ComposedProps> {
  state = {
    activeColorPicker: false
  };

  componentDidMount() {
    Emitter.on(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleHideColorPicker);
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleHideColorPicker);
  }

  handleHideColorPicker = () => {
    this.setState({
      activeColorPicker: false
    });
  };

  handleToggleColorPicker = () => {
    this.setState(({ activeColorPicker }) => ({
      activeColorPicker: !activeColorPicker
    }));
  };

  renderShapeRectangle = () => {
    const { activeColorPicker } = this.state;
    const { colorKey, formValues, dataFor, dataTip, disableAlpha } = this.props;

    const colorValue = _.get(formValues, colorKey, '');
    let colorValueWithoutA = colorValue;
    if (isHexWithAlpha(colorValue)) {
      colorValueWithoutA = colorValue.slice(0, colorValue.length - 2);
    }
    const rgbObject = hexToRgb(colorValueWithoutA);
    const contrastColorText = getContrastColor(rgbObject);

    const activator = (
      <div
        className="color-picker-item__box-color"
        style={{ backgroundColor: colorValueWithoutA, color: contrastColorText }}
        onClick={this.handleToggleColorPicker}
        data-for={dataFor}
        data-tip={dataTip}
        data-tooltip-offset={5}>
        <IconDrop style={{ fill: contrastColorText }} />
        <span className="color-picker-item__value">{colorValueWithoutA}</span>
      </div>
    );
    const colorPickerItemClass = Classnames('color-picker-item color-picker-item--rectangle');

    return (
      <div className={colorPickerItemClass}>
        <Popover
          active={activeColorPicker}
          activator={activator}
          onClose={this.handleToggleColorPicker}
          preferredAlignment="left">
          <Field
            name={colorKey}
            component={RenderColorCustomPicker}
            color={colorValue}
            // onChange={this.handleChangeColor}
            normalize={color => {
              return disableAlpha
                ? String(color.hex).toUpperCase()
                : String(RGBAToHex(color.rgb)).toUpperCase();
            }}
            disableAlpha={disableAlpha}
          />
        </Popover>
      </div>
    );
  };

  renderEmptyPicker = () => {
    const { activeColorPicker } = this.state;
    const { colorKey, formValues, dataFor, dataTip, disableAlpha } = this.props;
    const colorValue = _.get(formValues, colorKey, '');

    const activator = (
      <div
        className="color-picker-item__empty"
        onClick={this.handleToggleColorPicker}
        data-for={dataFor}
        data-tip={dataTip}
        data-tooltip-offset={5}>
        <Icon source={MobilePlusMajor} />
      </div>
    );
    const colorPickerItemClass = Classnames('color-picker-item color-picker-item--rectangle');

    return (
      <div className={colorPickerItemClass}>
        <Popover
          active={activeColorPicker}
          activator={activator}
          onClose={this.handleToggleColorPicker}
          preferredAlignment="left">
          <Field
            name={colorKey}
            component={RenderColorCustomPicker}
            color={colorValue}
            // onChange={this.handleChangeColor}
            normalize={color => {
              return disableAlpha
                ? String(color.hex).toUpperCase()
                : String(RGBAToHex(color.rgb)).toUpperCase();
            }}
            disableAlpha={disableAlpha}
          />
        </Popover>
      </div>
    );
  };

  render() {
    const { colorKey, formValues } = this.props;
    const colorValue = _.get(formValues, colorKey, '');
    if (!colorValue) {
      return this.renderEmptyPicker();
    }

    return this.renderShapeRectangle();
  }
}

export default withI18n()(ColorPickerItem);
